import React, { useState } from 'react';
import { ConciergeBell, ArrowRight, MapPin, Calendar, Sparkles } from 'lucide-react';
import Modal from '../ui/Modal';
import { buildConciergeTallyUrl, submitConciergeRequest } from '../../api/concierge';

/**
 * "Atlas Plan" hand-off button + modal.
 * Opens the Tally enquiry form pre-filled with the attached trip details.
 *
 * Props:
 *   itineraryId, tripTitle, destination, tripLength — the trip to attach
 *   tripLink   — override the attached link (defaults to /itinerary/:id)
 *   source     — 'planner' | 'itinerary' | 'atlas_file' | 'generic'
 *   className  — style the trigger button (defaults to a coral pill)
 *   label      — trigger text (default "Atlas Plan")
 *   compact    — smaller icon-forward trigger
 */
export default function SendToConcierge({
  itineraryId = null,
  tripTitle = null,
  destination = null,
  tripLength = null,
  tripLink = null,
  source = 'generic',
  className = '',
  label = 'Atlas Plan',
  compact = false,
}) {
  const [open, setOpen] = useState(false);

  const resolvedLink =
    tripLink ||
    (itineraryId ? `${window.location.origin}/itinerary/${itineraryId}` : null);

  const tallyUrl = buildConciergeTallyUrl({
    tripTitle,
    destination,
    tripLength,
    tripLink: resolvedLink,
  });

  const handleSend = () => {
    setOpen(false);
    // Best-effort: record the lead even if Tally is blocked, fails, or is down.
    // Never blocks the Tally hand-off below.
    submitConciergeRequest({
      itineraryId,
      source,
      tripTitle,
      destination,
      tripLength,
      tripLink: resolvedLink,
    }).catch((err) => {
      console.error('Failed to save concierge request:', err);
    });
  };

  const defaultTrigger =
    'inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all hover:scale-[1.02] bg-coral-400 hover:bg-coral-500 text-white ' +
    (compact ? 'px-3 py-1.5 text-xs' : 'px-5 py-2.5 text-sm');

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className || defaultTrigger}>
        <ConciergeBell size={compact ? 13 : 16} />
        {label}
      </button>

      <Modal isOpen={open} onClose={() => setOpen(false)} title="Get a free Atlas Plan quote" size="sm">
        <div>
          <p className="text-sm text-charcoal-400 leading-relaxed mb-5">
            Our Atlas Plan team will take your itinerary, perfect every detail — hotels, pacing, experiences — and send you a free personalised quote within 24 hours.
          </p>

          {(tripTitle || destination) && (
            <div className="rounded-2xl border border-platinum-200 bg-platinum-50/60 p-4 mb-5">
              <p className="text-[11px] font-bold uppercase tracking-widest text-coral-400 mb-1.5">Your trip</p>
              <p className="font-bold text-charcoal-500 leading-snug">{tripTitle || 'Your trip'}</p>
              <div className="flex flex-wrap gap-3 mt-1.5 text-xs text-platinum-600">
                {destination && <span className="flex items-center gap-1"><MapPin size={12} />{destination}</span>}
                {tripLength && <span className="flex items-center gap-1"><Calendar size={12} />{tripLength} days</span>}
              </div>
            </div>
          )}

          <a
            href={tallyUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleSend}
            className="flex items-center justify-center gap-2 w-full px-6 py-3 rounded-xl bg-coral-400 hover:bg-coral-500 text-white text-sm font-bold transition-all"
          >
            Send to our team <ArrowRight size={15} />
          </a>

          <p className="flex items-center justify-center gap-1.5 text-[11px] text-platinum-400 mt-4">
            <Sparkles size={11} /> Free quote · No commitment · Reply within 24 hours
          </p>
        </div>
      </Modal>
    </>
  );
}
